import { getPaymentMethods } from '../../core/payments/payments-api';
import { startDeposit } from '../../core/payments/deposit-flow';
import { mountPaymentMethodPicker } from '../../core/payments/widgets/payment-method-picker';
import type { PaymentMethodPickerHandle } from '../../core/payments/widgets/payment-method-picker';
import type { DepositMethod, DepositMethodId, PaymentMethodsResponse } from '../../core/payments/types';
import { quoteDeltaVForCents } from './shop-api';
import type { DeltaVPackSku } from './shop-api';
import { t } from '../../engine/i18n/i18n';

export interface CheckoutState {
  cents: number;
  currency: string;
}

export function formatEur(cents: number): string {
  return `€${(cents / 100).toFixed(2)}`;
}

export function parseAmountToCents(raw: string): number | null {
  const cleaned = raw.replace(/[^0-9.]/g, '');
  if (cleaned === '' || cleaned === '.') return null;
  const value = Number(cleaned);
  if (!Number.isFinite(value)) return null;
  return Math.round(value * 100);
}

export function clampCents(cents: number, min: number, max: number): number {
  return Math.min(Math.max(Math.floor(cents), min), max);
}

interface CheckoutViewArgs {
  state: CheckoutState;
  packs: DeltaVPackSku[];
  minCents: number;
  maxCents: number;
  busy: boolean;
  methods: DepositMethod[] | null;
}

export function checkoutPanelHtml({ state, packs, minCents, maxCents, busy, methods }: CheckoutViewArgs): string | null {
  if (!minCents || !maxCents || maxCents <= minCents) return null;

  const cents = clampCents(state.cents, minCents, maxCents);
  const deltaV = quoteDeltaVForCents(cents, packs, minCents, maxCents);
  const per100 = quoteDeltaVForCents(10_000, packs, minCents, maxCents) / 100;

  const hasPayments = methods && methods.length > 0;

  return `
    <div class="glass-panel p-4 flex flex-col gap-4" data-checkout>
      <div class="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <div class="text-[10px] text-text-dim uppercase tracking-wide">${t('shop.checkout.customAmount')}</div>
          <div class="font-heading text-2xl text-gold tabular-nums" data-checkout-deltav>${deltaV.toLocaleString('en-US')}</div>
          <div class="text-[10px] text-text-dim">${t('shop.checkout.deltaVPerEuro', { amount: per100.toFixed(0) })}</div>
        </div>
        <div class="font-body text-xl text-text tabular-nums" data-checkout-price>${formatEur(cents)}</div>
      </div>

      <div class="flex flex-col gap-1">
        <input type="range" data-checkout-slider
          min="${minCents}" max="${maxCents}" step="100" value="${cents}"
          aria-label="${t('shop.checkout.amountAria')}"
          class="w-full accent-gold touch-manipulation" />
        <div class="flex justify-between text-[10px] text-text-dim tabular-nums">
          <span>${formatEur(minCents)}</span>
          <span>${formatEur(maxCents)}</span>
        </div>
      </div>

      <div class="flex flex-wrap items-center gap-3">
        <label class="flex items-center gap-1 shrink-0">
          <span class="sr-only">${t('shop.checkout.amountEuros')}</span>
          <span class="text-text-dim text-sm" aria-hidden="true">&euro;</span>
          <input type="text" inputmode="decimal" data-checkout-amount
            value="${(cents / 100).toFixed(2)}"
            class="w-24 min-h-11 px-2 bg-surface-2 border border-border rounded text-text text-sm tabular-nums focus:border-gold outline-none" />
        </label>

        <button type="button" data-action="buy-custom-delta-v" ${busy || !hasPayments ? 'disabled' : ''}
          class="font-button min-h-11 px-6 ml-auto bg-gold text-bg text-sm rounded transition-colors duration-[120ms] hover:bg-gold-bright disabled:bg-border disabled:text-text-dim disabled:cursor-not-allowed touch-manipulation">
          ${hasPayments ? t('shop.checkout.payNow') : t('shop.checkout.noMethods')}
        </button>
      </div>

      <div data-checkout-methods></div>

      ${!hasPayments ? `<p class="text-[10px] text-text-dim leading-relaxed">
        ${t('shop.checkout.noMethodsHelp')}
      </p>` : ''}
    </div>
  `;
}

/**
 * Manages real payment method picker inside the checkout panel.
 * Call after the checkout HTML is in the DOM.
 */
export function mountCheckoutMethods(
  container: HTMLElement,
  methods: DepositMethod[],
  currency: string,
): PaymentMethodPickerHandle | null {
  const el = container.querySelector('[data-checkout-methods]') as HTMLElement | null;
  if (!el || methods.length === 0) return null;

  return mountPaymentMethodPicker(el, {
    methods,
    currency,
    selected: methods.find((m) => m.enabled)?.id ?? null,
    showDisabled: false,
    onSelect() {},
    onCurrencySwitch() {},
  });
}

/**
 * Execute a real deposit for a Delta-V pack.
 */
export async function depositPack(
  packId: string,
  method: DepositMethodId,
  currency: string,
): Promise<void> {
  await startDeposit({
    method,
    productId: packId,
    currency,
    returnPath: '/payment-result',
  });
}

/**
 * Execute a real deposit for a custom Delta-V amount.
 */
export async function depositCustom(
  amountCents: number,
  method: DepositMethodId,
  currency: string,
): Promise<void> {
  await startDeposit({
    method,
    amountCents,
    currency,
    returnPath: '/payment-result',
  });
}

/**
 * Load payment methods from core. Returns null if payments not adopted.
 */
export async function loadPaymentMethods(): Promise<PaymentMethodsResponse | null> {
  try {
    return await getPaymentMethods();
  } catch {
    return null;
  }
}
